import BILLING_API from 'api/billing/v1'
import SERVICE_API_V2 from 'api/services/v2'
import USER_API from 'api/user/v1'
import ACCOUNTS_MARKETPLACE_API from 'api/accounts-marketplace/v1'
import { startSubmit, stopSubmit } from 'redux-form'
import {
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'
import { cancellableCall } from 'store/saga-utils/cancellableCall.saga'
import extractErrors from 'utils/extractErrors'
import makeRedirectUrl from 'utils/makeRedirectUrl'
import { notificationShow } from 'modules/Notifications/actions'
import { generatePath } from 'react-router'
import { ROUTES } from 'constants/routes'
import * as Sentry from '@sentry/browser'
import {
  ACCOUNT_MARKETPLACE_GET_PAYMENT_METHODS,
  ACCOUNT_MARKETPLACE_ORDER_PROCESS,
  CALCULATE,
  CALCULATE_ACCOUNT_MARKETPLACE_ORDER,
  CALCULATE_ACCOUNTS,
  CALCULATE_FEE,
  DEPOSIT_CREATION_FEE,
  GET_ACCOUNT_DATA,
  GET_NETWORK_ACCOUNT_DATA,
  GET_PAYMENT_METHODS,
  SET_ACCOUNT_DATA,
  SET_DATA_LOADING,
  SET_NETWORK_ACCOUNT_DATA,
  SET_PAYMENT_METHODS,
  TOP_UP,
} from 'modules/Forms/Billing/types'

function getParams({
  accountId = null,
  accountAmount = null,
  is_payment_profile = false,
  payment_profile_id = null,
  method = null,
  is_wallets = false,
  wallet_id = null,
  accounts = null,
  networkId = null,
  payment_amount,
}) {
  const amountType = accountAmount ? 'account_amount' : 'payment_amount'
  const isMultipleAccounts = accounts?.length > 0
  const data = {}

  if (method) {
    data.method = method
  }

  if (payment_amount) {
    data[amountType] = payment_amount
  }

  if (is_payment_profile && payment_profile_id) {
    data.payment_profile_id = payment_profile_id
  }

  if (
    is_wallets &&
    wallet_id &&
    wallet_id !== 'null' &&
    method !== 'user_referral_balance'
  ) {
    data.wallet_id = wallet_id
  }

  if (isMultipleAccounts) {
    data.accounts = accounts
  }

  const params = {
    data,
  }

  if (accountId) {
    params.accountId = accountId
  }
  if (networkId) {
    params.networkId = networkId
  }

  return params
}

function getAccountsMarketplaceOrderParams({
  items = [],
  currency,
  is_payment_profile = false,
  payment_profile_id = null,
  method = null,
  is_wallets = false,
  wallet_id = null,
}) {
  const data = {
    items,
    currency,
  }

  if (method) {
    data.method = method
  }

  if (is_payment_profile && payment_profile_id) {
    data.payment_profile_id = payment_profile_id
  }

  if (
    is_wallets &&
    wallet_id &&
    wallet_id !== 'null' &&
    method !== 'user_referral_balance'
  ) {
    data.wallet_id = wallet_id
  }

  return {
    data,
  }
}

function* calculate({ payload, ...rest }) {
  const {
    accountId = null,
    accountAmount = null,
    formName,
    delayTime = 0,
    meta: { resolve, reject } = {},
  } = payload

  yield delay(delayTime)

  const params = yield call(getParams, { ...payload })

  try {
    yield put(startSubmit(formName))

    const { response, error, canceled } = yield cancellableCall(
      accountId ? BILLING_API.calculateAccount : BILLING_API.calculateDeposit,
      params,
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    if (accountAmount) {
      const amount = response.data.transfer.payment.value
      yield call(calculate, {
        ...rest,
        payload: { ...payload, accountAmount: null, amount },
      })
    }
    resolve(response)
    yield put(stopSubmit(formName))
  } catch (error) {
    const { fields, form } = extractErrors(error.errors)

    if (Object.keys(fields).length) {
      yield put(stopSubmit(formName, fields))
      reject()
      return
    }

    yield put(
      notificationShow({
        type: 'error',
        message: form || 'common::notifications.data_fetch_failed',
      }),
    )
    reject()

    yield put(stopSubmit(formName))
    console.logXHR(error)
  }
}

function* calculateAccountMarketplaceOrder({ payload }) {
  const { formName, delayTime = 0, meta: { resolve, reject } = {} } = payload

  yield delay(delayTime)

  const params = yield call(getAccountsMarketplaceOrderParams, { ...payload })

  try {
    yield put(startSubmit(formName))

    const { response, error, canceled } = yield cancellableCall(
      BILLING_API.accountsMarketplaceCalculate,
      params,
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    resolve(response)
    yield put(stopSubmit(formName))
  } catch (error) {
    const { fields, form } = extractErrors(error.errors)

    if (Object.keys(fields).length) {
      yield put(stopSubmit(formName, fields))
      reject()
      return
    }

    yield put(
      notificationShow({
        type: 'error',
        message: form || 'common::notifications.data_fetch_failed',
      }),
    )
    reject()

    yield put(stopSubmit(formName))
    console.logXHR(error)
  }
}

function* calculateFee({ payload }) {
  const { formName, delayTime = 0, meta: { resolve, reject } = {} } = payload

  yield delay(delayTime)

  const params = yield call(getParams, { ...payload })
  params.data.currency = payload.currency

  try {
    yield put(startSubmit(formName))
    const { response, error, canceled } = yield cancellableCall(
      BILLING_API.calculateDepositCreationFee,
      params,
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    resolve(response)
    yield put(stopSubmit(formName))
  } catch (error) {
    const { fields, form } = extractErrors(error.errors)

    if (Object.keys(fields).length) {
      yield put(stopSubmit(formName, fields))
      reject()
      return
    }

    yield put(
      notificationShow({
        type: 'error',
        message: form || 'common::notifications.data_fetch_failed',
      }),
    )
    reject()

    yield put(stopSubmit(formName))
    console.logXHR(error)
  }
}

function* calculateAccounts({ payload, ...rest }) {
  const {
    accountAmount = null,
    formName,
    delayTime = 0,
    meta: { resolve, reject } = {},
  } = payload

  yield delay(delayTime)

  const params = yield call(getParams, { ...payload })

  try {
    yield put(startSubmit(formName))

    const { response, error, canceled } = yield cancellableCall(
      BILLING_API.calculateAccounts,
      params,
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    if (accountAmount) {
      const amount = response.data.transfer.payment.value
      yield call(calculateAccounts, {
        ...rest,
        payload: { ...payload, accountAmount: null, amount },
      })
    }
    resolve(response)
    yield put(stopSubmit(formName))
  } catch (error) {
    const { fields, form } = extractErrors(error.errors)

    if (Object.keys(fields).length) {
      yield put(stopSubmit(formName, fields))
      reject({ error: fields })

      return
    }

    reject({ error: form || 'common::notifications.data_fetch_failed' })

    yield put(stopSubmit(formName))
    console.logXHR(error)
  }
}

function* topUp({ payload }) {
  const {
    accountId = null,
    payment_amount = null,
    is_payment_profile = false,
    payment_profile_id = null,
    method = null,
    is_wallets = false,
    wallet_id = null,
    networkType = '',
    isMultipleAccounts,
    meta: { resolve, reject } = {},
    formName,
  } = payload

  const { i18n } = yield select(({ app }) => app)
  const data = {}

  if (method) {
    data.method = method
  }

  if (is_payment_profile && payment_profile_id) {
    data.payment_profile_id = payment_profile_id
  }

  if (
    is_wallets &&
    wallet_id &&
    wallet_id !== 'null' &&
    method !== 'user_referral_balance'
  ) {
    data.wallet_id = wallet_id
  }

  if (payment_amount) {
    data.payment_amount = payment_amount
  }

  const params = {
    data,
  }

  if (accountId) {
    params.accountId = accountId
  }

  let submitApiRoute = null

  switch (formName) {
    case 'billing':
    case 'accounts-billing':
      submitApiRoute = accountId
        ? BILLING_API.topUpAccount
        : BILLING_API.topUpDeposit
      break
    case 'deposit_creation_fee':
      submitApiRoute = BILLING_API.depositCreationFee
      break
  }

  try {
    yield put(startSubmit(formName))

    const { response, error } = yield call(submitApiRoute, params)

    if (error || !response?.data?.result_params) {
      Sentry.captureMessage(
        `topUp ${accountId ? 'account' : 'deposit'} error. request_id: ` +
          error?.errors?.[0] ?? 'undefined request_id',
      )
      throw error
    }

    const redirect_url = makeRedirectUrl({
      i18n,
      result_params: response.data.result_params,
      networkType,
      method,
    })

    global.yaCounter32793525 &&
      global.yaCounter32793525.reachGoal('send-user-to-payment-page')

    global.dataLayer &&
      global.dataLayer.push({ event: 'send-user-to-payment-page' })

    resolve({
      status: response.status,
      redirect_url,
      pay_url: response.data.pay_url,
      invoice: response.data.result_params?.invoice || null,
    })
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    if (fields && Object.keys(fields).length) {
      if (isMultipleAccounts) {
        reject(fields)
      } else {
        yield put(stopSubmit(formName, fields))
        reject()
      }

      return
    }

    if (isMultipleAccounts) {
      reject(form || 'billing::notifications.processing_error')
    } else {
      yield put(
        notificationShow({
          type: 'error',
          message: form || 'billing::notifications.processing_error',
          delay: 10000,
        }),
      )
    }
    yield put(stopSubmit(formName))
    console.logXHR(error)
  }
}

function* accountMarketplaceOrderProcessAndDeposit({ payload }) {
  const {
    method,
    items = [],
    is_payment_profile,
    payment_profile_id,
    is_wallets,
    wallet_id,
    payment_amount,
    meta: { resolve, reject } = {},
  } = payload

  const formName = 'account-marketplace'
  const { i18n } = yield select(({ app }) => app)
  const orderData = {}

  if (method) {
    orderData.method = method
  }

  if (is_payment_profile && payment_profile_id) {
    orderData.payment_profile_id = payment_profile_id
  }

  if (
    is_wallets &&
    wallet_id &&
    wallet_id !== 'null' &&
    method !== 'user_referral_balance'
  ) {
    orderData.wallet_id = wallet_id
  }

  if (payment_amount) {
    orderData.payment_amount = payment_amount
  }

  try {
    yield put(startSubmit(formName))

    //create new order
    const { response: newOrderResponse, error: newOrderError } = yield call(
      ACCOUNTS_MARKETPLACE_API.createOrder,
      { data: { items } },
    )

    if (newOrderError) {
      throw newOrderError
    }

    //and deposit new order here
    const { response: orderDepositResponse, error: orderDepositError } =
      yield call(BILLING_API.accountsMarketplaceOrderDeposit, {
        order_id: newOrderResponse.data.order_id,
        data: orderData,
      })

    if (orderDepositError) {
      throw orderDepositError
    }

    const { response: productResponse, error: productError } = yield call(
      ACCOUNTS_MARKETPLACE_API.fetchProductById,
      { id: items[0].product_id },
    )

    if (productError) {
      throw productError
    }

    const redirect_url = productResponse?.data
      ? generatePath(ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY_PRODUCTS, {
          i18n,
          categoryId: productResponse?.data?.category_id,
        })
      : generatePath(ROUTES.ACCOUNTS_MARKETPLACE, { i18n })

    global.yaCounter32793525 &&
      global.yaCounter32793525.reachGoal('send-user-to-payment-page')

    global.dataLayer &&
      global.dataLayer.push({ event: 'send-user-to-payment-page' })

    resolve({
      status: orderDepositResponse.status,
      redirect_url,
      pay_url: orderDepositResponse.data.pay_url,
    })
  } catch (error) {
    Sentry.captureMessage(`account marketplace order deposit error`)

    const { form, fields } = extractErrors(error.errors)

    if (fields && Object.keys(fields).length) {
      yield put(stopSubmit(formName, fields))
      reject()

      return
    }

    yield put(
      notificationShow({
        type: 'error',
        message: form || 'common::notifications.data_fetch_failed',
        delay: 10000,
      }),
    )
    reject()

    yield put(stopSubmit(formName))
    console.logXHR(error)
  }
}

function* getPaymentMethods({
  payload: { accountId = null, networkId = null } = {},
}) {
  yield put({
    type: SET_DATA_LOADING,
    payload: { loading: true },
  })

  const params = {}

  if (accountId) {
    params.accountId = accountId
  }

  if (networkId) {
    params.networkId = networkId
  }

  try {
    const { response, error, canceled } = yield cancellableCall(
      accountId
        ? BILLING_API.fetchAccountPaymentMethods
        : networkId
        ? BILLING_API.fetchNetworkPaymentMethods
        : BILLING_API.fetchPaymentMethods,
      params,
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    yield put({
      type: SET_PAYMENT_METHODS,
      payload: response.data.map((method) => {
        if (method.method === 'admitad') {
          if (!method.params) {
            method.params = {}
          }
          method.params.wallets = []
        }

        return method
      }),
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )

    console.logXHR(error)
  } finally {
    yield put({
      type: SET_DATA_LOADING,
      payload: { loading: false },
    })
  }
}

function* getAccountsMarketplacePaymentMethods() {
  yield put({
    type: SET_DATA_LOADING,
    payload: { loading: true },
  })

  const params = {}

  try {
    const { response, error } = yield call(
      BILLING_API.fetchAccountsMarketplacePaymentMethods,
      params,
    )

    if (error) {
      throw error
    }

    yield put({
      type: SET_PAYMENT_METHODS,
      payload: response.data.map((method) => {
        if (method.method === 'admitad') {
          if (!method.params) {
            method.params = {}
          }
          method.params.wallets = []
        }

        return method
      }),
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )

    console.logXHR(error)
  } finally {
    yield put({
      type: SET_DATA_LOADING,
      payload: { loading: false },
    })
  }
}

function* getNetworkAccountData({ payload }) {
  yield put({
    type: SET_NETWORK_ACCOUNT_DATA,
    payload: { loading: true },
  })

  try {
    const { response, error } = yield call(
      SERVICE_API_V2.getNetworkAccountById,
      payload,
    )

    if (error) {
      if (error.status === 404) {
        yield put({
          type: SET_NETWORK_ACCOUNT_DATA,
          payload: { status: 404, loaded: true },
        })
      }

      throw error
    }

    yield put({
      type: SET_NETWORK_ACCOUNT_DATA,
      payload: { ...response.data, loaded: true },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: SET_NETWORK_ACCOUNT_DATA,
      payload: { loading: false },
    })
  }
}

function* getAccountData({ payload }) {
  yield put({
    type: SET_ACCOUNT_DATA,
    payload: { loading: true, loaded: false },
  })

  try {
    const { response, error } = yield call(USER_API.fetchById, {
      ...payload,
    })

    if (error) {
      if (error.status === 404) {
        yield put({
          type: SET_ACCOUNT_DATA,
          payload: { status: 404, loaded: true },
        })
      }
      throw error
    }

    yield put({
      type: SET_ACCOUNT_DATA,
      payload: { ...response.data, loaded: true },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: SET_ACCOUNT_DATA,
      payload: { loading: false },
    })
  }
}

export default function* Sagas() {
  yield takeLatest(CALCULATE, calculate)
  yield takeLatest(CALCULATE_ACCOUNTS, calculateAccounts)
  yield takeLatest(CALCULATE_FEE, calculateFee)
  yield takeLatest(
    CALCULATE_ACCOUNT_MARKETPLACE_ORDER,
    calculateAccountMarketplaceOrder,
  )

  yield takeEvery(TOP_UP, topUp)
  yield takeLatest(GET_PAYMENT_METHODS, getPaymentMethods)
  yield takeLatest(GET_NETWORK_ACCOUNT_DATA, getNetworkAccountData)
  yield takeLatest(GET_ACCOUNT_DATA, getAccountData)
  yield takeLatest(DEPOSIT_CREATION_FEE, topUp)
  yield takeLatest(
    ACCOUNT_MARKETPLACE_GET_PAYMENT_METHODS,
    getAccountsMarketplacePaymentMethods,
  )
  yield takeLatest(
    ACCOUNT_MARKETPLACE_ORDER_PROCESS,
    accountMarketplaceOrderProcessAndDeposit,
  )
}
